import React, { useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { SiteHeader, SiteFooter } from 'pages/SiteFrame';

/**
 * The header and footer as a visitor will actually meet them.
 *
 * This used to be a hand-drawn approximation — its own markup, its own
 * spacing, its own idea of what a menu looks like. So every setting added to
 * the real bar had to be added here too, and the ones that were not simply
 * did not appear: someone changed the spacing, the link style, the glass, and
 * the preview sat there unchanged, which reads as the setting doing nothing.
 *
 * It renders the real components now. There is one header in this codebase,
 * and the preview cannot drift from the site because it IS the site.
 *
 * Clicks are swallowed: everything here is content being previewed, not
 * controls, and following a menu link would navigate out of the editor.
 */
export default function GlobalPreview({ value, brandName, brandLogo }) {
  const { header, footer, headerEnabled, footerEnabled } = value;
  /**
   * Which theme the preview is showing.
   *
   * The dark colours are now something you can set, and setting a colour you
   * cannot see is guessing. This is also the only place the derivation is
   * visible before it reaches a visitor.
   */
  const [dark, setDark] = useState(false);
  const theme = { enabled: header?.themeToggle?.enabled !== false, isDark: dark, toggle: () => setDark((v) => !v) };

  return (
    <Box>
      <ToggleButtonGroup
        size='small'
        exclusive
        value={dark ? 'dark' : 'light'}
        onChange={(_e, v) => v && setDark(v === 'dark')}
        sx={{ mb: '10px' }}
      >
        <ToggleButton value='light'>Light</ToggleButton>
        <ToggleButton value='dark'>Dark</ToggleButton>
      </ToggleButtonGroup>
      <Box
        onClickCapture={(e) => { e.preventDefault(); e.stopPropagation(); }}
        sx={{
          border: '1px solid', borderColor: 'divider', borderRadius: '12px',
          overflow: 'hidden',
          // The public frame is built for a page, not a 360px panel. A
          // container query would be the honest fix; until the frame is
          // written against one, this keeps the proportions readable.
          // The visitor's page, not the dashboard's: it follows the switch
          // above, never the dashboard's own light/dark setting.
          bgcolor: dark ? '#0f1115' : '#fff',
        }}
      >
        {headerEnabled ? (
          <SiteHeader
            header={header}
            brandName={brandName}
            brandLogo={brandLogo}
            theme={theme}
          />
        ) : (
          <DisabledBand>Header is switched off — pages start with their own content.</DisabledBand>
        )}

        {/* Something for a translucent bar to sit over, and a reminder that
            the middle of the page is not edited here. Frosted glass is
            invisible against a blank panel, so a plain grey band would show a
            solid bar whatever was configured. */}
        <Box
          sx={{
            py: '34px',
            px: '20px',
            textAlign: 'center',
            background: 'repeating-linear-gradient(135deg, #eef2ff 0 18px, #e0e7ff 18px 36px)',
          }}
        >
          <Typography variant='caption' color='text.secondary'>
            Your page content appears here
          </Typography>
        </Box>

        {footerEnabled ? (
          <SiteFooter footer={footer} brandName={brandName} theme={theme} />
        ) : (
          <DisabledBand>Footer is switched off.</DisabledBand>
        )}
      </Box>

      <Typography variant='caption' color='text.secondary' display='block' mt='8px'>
        This frame wraps every page on your site.
        {header?.sticky ? ' The bar stays on screen as visitors scroll.' : ''}
      </Typography>
    </Box>
  );
}

function DisabledBand({ children }) {
  return (
    <Box sx={{ p: '14px 16px', bgcolor: 'action.hover' }}>
      <Typography variant='caption' color='text.secondary'>{children}</Typography>
    </Box>
  );
}
